import React from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { getEnv } from 'mobx-state-tree';
import { toast } from 'react-toastify';

import {
    useGroupsUIStore,
    useProceduresUIStore,
    useProcedureUIStore,
    useUserUIStore,
    useUsersGridUIStore,
} from '@core/useStores';
import { IStoresEnv } from '@core/storesEnv';
import { useStyles } from './GroupHeader.styles';
import { ThemeProps } from '@styles/theme';
import { Button } from '@shared/components/Button';
import IconButton from '@mui/material/IconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useBreakpointDown } from '@shared/hooks/useBreakpoint';
import { ToastMessage } from '@shared/components/Toast';
import { DeleteConfirmPopover } from '@shared/components/DeleteConfirmPopover';
import { ProcedureVersionAssignment } from 'Procedures/services';
import { messages } from 'Groups/groups.messages';

type UserEditSnapshot = {
    id: string;
    firstName: string;
    lastName: string;
    department: string;
    email: string;
    roleId?: string;
    groupIds: string[];
    procedureIds: string[];
};

type ProcedureEditSnapshot = {
    id: string;
    assignments: ProcedureVersionAssignment[];
};

export const GroupHeader: React.FC = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const isPhoneLayout = useBreakpointDown('mobile');

    const groupsUIStore = useGroupsUIStore();
    const usersGridUIStore = useUsersGridUIStore();
    const proceduresGridUIStore = useProceduresUIStore();
    const userUIStore = useUserUIStore();
    const procedureUIStore = useProcedureUIStore();
    const [isRestoring, setIsRestoring] = React.useState(false);
    const [isSavingName, setIsSavingName] = React.useState(false);
    const [editedName, setEditedName] = React.useState('');
    const [deleteAnchorEl, setDeleteAnchorEl] = React.useState<HTMLElement | null>(null);
    const deleteButtonRef = React.useRef<HTMLDivElement>(null);
    const usersSnapshotRef = React.useRef<UserEditSnapshot[]>([]);
    const proceduresSnapshotRef = React.useRef<ProcedureEditSnapshot[]>([]);
    const { filtersOptions } = getEnv<IStoresEnv>(groupsUIStore);

    const captureUsersSnapshot = React.useCallback(() => {
        usersSnapshotRef.current = usersGridUIStore.users.map(user => ({
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            department: user.department,
            email: user.email,
            roleId: user.roles[0]?.id,
            groupIds: user.groups.map(group => group.id),
            procedureIds: user.procedures.map(procedure => procedure.id),
        }));
    }, [usersGridUIStore.users]);

    const captureProceduresSnapshot = React.useCallback(() => {
        proceduresSnapshotRef.current = proceduresGridUIStore.procedures.map(procedure => ({
            id: procedure.id,
            assignments: [
                ...procedure.versions.map(version => ({
                    id: procedure.id,
                    versionId: version.id,
                    userIds: procedure.users
                        .filter(user => user.data?.assignedVersion === version.id)
                        .map(user => user.id),
                    groupIds: procedure.groups
                        .filter(group => group.data?.assignedVersion === version.id)
                        .map(group => group.id),
                })),
                {
                    id: procedure.id,
                    versionId: null,
                    userIds: procedure.users
                        .filter(user => user.data?.assignedVersion == null)
                        .map(user => user.id),
                    groupIds: procedure.groups
                        .filter(group => group.data?.assignedVersion == null)
                        .map(group => group.id),
                },
            ],
        }));
    }, [proceduresGridUIStore.procedures]);

    const restoreMassEditSnapshot = React.useCallback(async () => {
        setIsRestoring(true);

        try {
            const usersToRestore = usersSnapshotRef.current.filter(snapshot => {
                const current = usersGridUIStore.users.find(u => u.id === snapshot.id);
                if (!current) {return false;}
                const currentGroupIds = current.groups.map(g => g.id).sort().join(',');
                const snapshotGroupIds = [...snapshot.groupIds].sort().join(',');

                return currentGroupIds !== snapshotGroupIds;
            });

            const proceduresToRestore = proceduresSnapshotRef.current.filter(snapshot => {
                const current = proceduresGridUIStore.procedures.find(p => p.id === snapshot.id);
                if (!current) {return false;}

                return snapshot.assignments.some(assignment => {
                    const currentGroupIds = current.groups
                        .filter((g: any) => g.data?.assignedVersion === assignment.versionId)
                        .map((g: any) => g.id)
                        .sort()
                        .join(',');
                    const snapshotGroupIds = (assignment.groupIds ?? []).slice().sort().join(',');

                    return currentGroupIds !== snapshotGroupIds;
                });
            });

            if (usersToRestore.length > 0 || proceduresToRestore.length > 0) {
                await Promise.all([
                    ...usersToRestore.map(user => userUIStore.editUser(user)),
                    ...proceduresToRestore.map(procedure =>
                        procedureUIStore.saveVersionAssignments(procedure.assignments)
                    ),
                ]);

                await Promise.all([
                    usersGridUIStore.load(),
                    usersGridUIStore.loadStatistics(),
                    proceduresGridUIStore.load(),
                    proceduresGridUIStore.loadStatistics(),
                    groupsUIStore.loadGroups(),
                ]);
            }
        } finally {
            setIsRestoring(false);
        }
    }, [
        groupsUIStore,
        procedureUIStore,
        proceduresGridUIStore,
        userUIStore,
        usersGridUIStore,
    ]);

    const onStartMassEdit = () => {
        captureUsersSnapshot();
        captureProceduresSnapshot();
        setEditedName(groupsUIStore.selectedGroup?.name ?? '');
        groupsUIStore.toggleMassEdit(true);
    };

    const saveGroupName = async (): Promise<boolean> => {
        const group = groupsUIStore.selectedGroup;
        const newName = editedName.trim();

        if (!group || newName === group.name) {return true;}

        if (!newName) {
            toast.error(
                <ToastMessage
                    message={messages['groupModal.error.required'](messages['groupModal.name.label'])}
                    type='error'
                />
            );

            return false;
        }

        setIsSavingName(true);
        try {
            const { data } = await groupsUIStore.loadGroup(group.id);
            const result = await groupsUIStore.editGroup({
                id: group.id,
                name: newName,
                userIds: data?.userIds ?? [],
                procedureIds: data?.procedureIds ?? [],
            });

            if (!result.success) {
                toast.error(
                    <ToastMessage
                        message={result.error?.body?.messages?.[0] ?? 'Failed to rename group'}
                        type='error'
                    />
                );

                return false;
            }

            toast.success(
                <ToastMessage
                    message={messages['groupModal.editAction.success'](newName, true)}
                    type='success'
                />
            );
            await groupsUIStore.loadGroups();
            filtersOptions.loadFilterOptions();

            return true;
        } finally {
            setIsSavingName(false);
        }
    };

    const onDeleteGroupConfirm = async () => {
        const id = groupsUIStore.selectedGroup?.id;
        const groupName = groupsUIStore.selectedGroup?.name;
        if (!id) {return;}
        setDeleteAnchorEl(null);
        const result = await groupsUIStore.deleteGroup(id);
        if (result.success) {
            toast.success(
                <ToastMessage
                    message={messages['group.delete.success'](groupName)}
                    type='success'
                />
            );
            groupsUIStore.unselectGroup();
            groupsUIStore.loadGroups();
            filtersOptions.loadFilterOptions();
        } else {
            toast.error(
                <ToastMessage
                    message={result.error?.body?.messages?.[0] ?? 'Failed to delete group'}
                    type='error'
                />
            );
        }
        groupsUIStore.clearEditGroupId();
    };

    const onDoneMassEdit = async () => {
        const renamed = await saveGroupName();
        if (!renamed) {return;}
        groupsUIStore.toggleMassEdit(false);
    };

    const onCancelMassEdit = async () => {
        await restoreMassEditSnapshot();
        groupsUIStore.toggleMassEdit(false);
    };

    return (
        <>
            <div className={styles.mainWrapper}>
                <Observer>
                    {() =>{
                        const groupTitle = groupsUIStore.selectedGroup ? groupsUIStore.selectedGroup?.name : 'No Group Selected';
                        const isRenaming = groupsUIStore.massEdit && !!groupsUIStore.selectedGroup;

                        return(
                            <div className={styles.headingWrapper}>
                                <div className={styles.titleWrapper}>
                                    {isRenaming ? (
                                        <TextField
                                            value={editedName}
                                            onChange={event => setEditedName(event.target.value)}
                                            onKeyDown={event => {
                                                if (event.key === 'Enter') {onDoneMassEdit();}
                                            }}
                                            placeholder={messages['groupModal.name.label']}
                                            size="small"
                                            className={styles.titleInput}
                                            disabled={isSavingName}
                                            inputProps={{ 'data-testid': 'group-name-input' }}
                                        />
                                    ) : (
                                        <Typography variant="h6">{groupTitle}</Typography>
                                    )}
                                </div>
                            </div>
                        )
                    }}
                </Observer>
                <Observer>
                    {() => (
                        <div className={styles.editWrapper}>
                            {groupsUIStore.massEdit && (
                                isPhoneLayout ? (
                                    <IconButton
                                        className={styles.iconButton}
                                        color="secondary"
                                        disabled={isRestoring || isSavingName}
                                        onClick={onCancelMassEdit}
                                        aria-label={isRestoring ? 'Restoring' : 'Cancel'}
                                        title={isRestoring ? 'Restoring...' : 'Cancel'}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                ) : (
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        className={styles.editButton}
                                        disabled={isRestoring || isSavingName}
                                        onClick={onCancelMassEdit}
                                    >
                                        {isRestoring ? 'Restoring...' : 'Cancel'}
                                    </Button>
                                )
                            )}
                            {!groupsUIStore.massEdit && groupsUIStore.selectedGroup && (
                                <div ref={deleteButtonRef}>
                                    {isPhoneLayout ? (
                                        <IconButton
                                            className={styles.iconButton}
                                            color="error"
                                            onClick={() => setDeleteAnchorEl(deleteButtonRef.current)}
                                            aria-label="Delete"
                                            title="Delete"
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    ) : (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            className={styles.editButton}
                                            onClick={() => setDeleteAnchorEl(deleteButtonRef.current)}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                    <DeleteConfirmPopover
                                        anchorEl={deleteAnchorEl}
                                        onConfirm={onDeleteGroupConfirm}
                                        onCancel={() => setDeleteAnchorEl(null)}
                                        message={messages['group.delete.confirmation.message'](groupsUIStore.selectedGroup?.name)}
                                        confirmText={messages['group.delete.confirmation.confirmText']}
                                    />
                                </div>
                            )}
                            {isPhoneLayout ? (
                                <IconButton
                                    className={styles.iconButtonPrimary}
                                    color="primary"
                                    disabled={groupsUIStore.selectedGroup === null || groupsUIStore.selectedGroup === undefined || isRestoring || isSavingName}
                                    onClick={groupsUIStore.massEdit ? onDoneMassEdit : onStartMassEdit}
                                    aria-label={groupsUIStore.massEdit ? (isSavingName ? 'Saving' : 'Done') : 'Edit'}
                                    title={groupsUIStore.massEdit ? (isSavingName ? 'Saving...' : 'Done') : 'Edit'}
                                >
                                    {groupsUIStore.massEdit ? <CheckIcon /> : <EditOutlinedIcon />}
                                </IconButton>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={styles.editButton}
                                    disabled={groupsUIStore.selectedGroup === null || groupsUIStore.selectedGroup === undefined || isRestoring || isSavingName}
                                    onClick={groupsUIStore.massEdit ? onDoneMassEdit : onStartMassEdit}
                                >
                                    {groupsUIStore.massEdit ? (isSavingName ? 'Saving...' : 'Done') : 'Edit'}
                                </Button>
                            )}
                        </div>
                    )}
                </Observer>
            </div>
        </>
    )
};
